import type {
  DashboardConsumptionSettingsResponse,
  DashboardConsumptionSummaryResponse,
} from '@protoss/shared';
import * as React from 'react';
import { dashboardConsumptionApi } from '../services/consumptionApi';

export interface UseDashboardConsumptionDataResult {
  settings: DashboardConsumptionSettingsResponse | null;
  summary: DashboardConsumptionSummaryResponse | null;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  syncError: string | null;
  reload: () => void;
  triggerSync: () => Promise<void>;
}

export function useDashboardConsumptionData(
  projectUuid: string
): UseDashboardConsumptionDataResult {
  const [settings, setSettings] = React.useState<DashboardConsumptionSettingsResponse | null>(null);
  const [summary, setSummary] = React.useState<DashboardConsumptionSummaryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [syncError, setSyncError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fetchedSettings, fetchedSummary] = await Promise.all([
        dashboardConsumptionApi.getSettings(projectUuid),
        dashboardConsumptionApi.getSummary(projectUuid),
      ]);
      setSettings(fetchedSettings);
      setSummary(fetchedSummary);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectUuid]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = React.useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await dashboardConsumptionApi.sync(projectUuid);
      const [fetchedSettings, fetchedSummary] = await Promise.all([
        dashboardConsumptionApi.getSettings(projectUuid),
        dashboardConsumptionApi.getSummary(projectUuid),
      ]);
      setSettings(fetchedSettings);
      setSummary(fetchedSummary);
    } catch (err) {
      setSyncError((err as Error).message);
    } finally {
      setSyncing(false);
    }
  }, [projectUuid]);

  return {
    settings,
    summary,
    loading,
    syncing,
    error,
    syncError,
    reload: () => void load(),
    triggerSync,
  };
}
